import { Box, Flex, Text } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/react'
import {
  Ban,
  ContactRound,
  Copy,
  Ellipsis,
  MessageSquareText,
  PhoneCall,
  Printer,
  Sparkles,
} from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  useBlockedNumbers,
  useCanBlockNumber,
  useFindBlockedNumber,
} from '~/atoms/BlockedNumbersState'
import { useModalState } from '~/atoms/ModalState'
import { ExtensionBadgeVariant } from '~/components/shared/componentStyles/ExtensionBadge.style'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import {
  DropdownActionButton,
  MangoButtonMenuItem,
} from '~/components/shared/Dropdown/DropdownActionButton'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { ContactsOperation } from '~/hooks/toast/eventHandlers/contactsToastEventHandler'
import { useClipboard } from '~/hooks/useClipboard'
import { useShowTexting } from '~/hooks/useCurrentPartner'
import { useEmitterService } from '~/hooks/useEmitterService'
import {
  ContactCellStyleValues,
  useContactCellStyles,
} from '~/pages/Calls/componentStyles/CallsListItem.style'
import { ContactDetails } from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'
import { Size } from '~/types/ThemeTypes'

interface ContactCellProps {
  columnStyles: Record<string, any>
  title: string
  subtitle: string
  contact?: ContactDetails
  number: string
  isExtension?: boolean
  isHovered?: boolean
  showMenu?: boolean
  isBotCall?: boolean
  /** Accessible name for the row's action menu button. */
  actionsLabel?: string
  role?: React.AriaRole
}

export const ContactCell = ({
  columnStyles,
  title,
  subtitle,
  contact,
  number,
  isExtension,
  isHovered,
  showMenu = true,
  isBotCall,
  actionsLabel,
  role,
}: ContactCellProps) => {
  const { t } = useTranslation()
  const { emit } = useEmitterService()

  const handleSuccess = () => {
    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Success,
      domain: NotificationDomain.CONTACTS,
      operation: ContactsOperation.ContactNumberCopiedToClipboard,
    })
  }

  const handleFailure = (reason?: any) => {
    console.error(reason)
    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Error,
      domain: NotificationDomain.CONTACTS,
      operation: ContactsOperation.ContactNumberCopiedToClipboard,
    })
  }

  const styles = useContactCellStyles()
  const { copyToClipboard } = useClipboard({
    onFailure: handleFailure,
    onSuccess: handleSuccess,
  })

  const { blockedNumbers, unblockNumber, blockNumberWithConfirmation } =
    useBlockedNumbers()
  const showTexting = useShowTexting()

  const [menuOpen, setMenuOpen] = React.useState(false)
  const handleMenuOpenChange = (isOpen: boolean) => {
    setMenuOpen(isOpen)
  }

  const {
    callModal: [, setCallModalState],
    faxModal: [faxModalState, setFaxModalState],
    messagingModal: [, setMessagingModalState],
  } = useModalState()

  const handleCopyNumber = () => {
    copyToClipboard(number ?? '')
  }

  const handleCallNumber = () => {
    const callee =
      contact !== undefined && contact.defaultNumber !== undefined
        ? { contactNumber: contact.defaultNumber, contact: contact }
        : number
    setCallModalState({
      open: true,
      callee: callee,
    })
  }

  const handleMessageNumber = () => {
    setMessagingModalState({ open: true, number })
  }

  const handleSendFax = () => {
    setFaxModalState({ ...faxModalState, open: true, number })
  }

  const isMangoUser = isExtension // TODO: We need a better way to know if this is an internal user that isn't using their extension

  const buttonMenuItem = (
    value: string,
    text: string,
    icon: React.ComponentType<any>,
    handle: () => void,
  ): MangoButtonMenuItem<string> => {
    const IconComp = icon
    return {
      value,
      content: (
        <MenuListItem
          primaryText={text}
          leftContent={
            <Icon color="fg.default">
              <IconComp size={16} />
            </Icon>
          }
        />
      ),
      onSelect: handle,
    }
  }

  const menuItems: MangoButtonMenuItem<string>[] = isMangoUser
    ? [
        buttonMenuItem(
          'call',
          t('contactCell.menu.callNumber'),
          PhoneCall,
          handleCallNumber,
        ),
        buttonMenuItem(
          'copy',
          t('contactCell.menu.copyNumber'),
          Copy,
          handleCopyNumber,
        ),
      ]
    : [
        buttonMenuItem(
          'call',
          t('contactCell.menu.callNumber'),
          PhoneCall,
          handleCallNumber,
        ),
        ...(showTexting
          ? [
              buttonMenuItem(
                'message',
                t('contactCell.menu.messageNumber'),
                MessageSquareText,
                handleMessageNumber,
              ),
            ]
          : []),
        buttonMenuItem(
          'fax',
          t('contactCell.menu.sendFax'),
          Printer,
          handleSendFax,
        ),
        buttonMenuItem(
          'copy',
          t('contactCell.menu.copyNumber'),
          Copy,
          handleCopyNumber,
        ),
      ]

  const canBlockNumber = useCanBlockNumber(number)
  const blockedNumber = useFindBlockedNumber(blockedNumbers, number)
  if (!isMangoUser) {
    if (blockedNumber) {
      menuItems.push(
        buttonMenuItem(
          'unblock',
          t('contactCell.menu.unblockNumber'),
          Ban,
          () => {
            unblockNumber(blockedNumber.id, blockedNumber.phoneNumber)
          },
        ),
      )
    } else if (canBlockNumber) {
      menuItems.push(
        buttonMenuItem('block', t('contactCell.menu.blockNumber'), Ban, () => {
          blockNumberWithConfirmation(number)
        }),
      )
    }
  }

  const showActionButton = (isHovered || menuOpen) && showMenu

  return (
    <Flex role={role} css={{ ...columnStyles, ...styles.container }}>
      <Flex css={styles.titleContainer}>
        {isBotCall && (
          <Icon css={styles.titleIcon}>
            <Sparkles size="100%" />
          </Icon>
        )}
        {contact && !isBotCall && (
          <Icon css={styles.titleIcon}>
            <ContactRound size="100%" />
          </Icon>
        )}
        <Text css={styles.titleText}>{title}</Text>
      </Flex>
      <Flex css={styles.subtitleContainer}>
        <Text css={styles.subtitleText}>{subtitle}</Text>
        {isExtension && (
          <ExtensionBadge
            extension={number}
            variant={ExtensionBadgeVariant.LightGray}
            size={Size.sm}
          />
        )}
        {showMenu && (
          <Box
            opacity={showActionButton ? '100%' : '0'}
            _hover={{ opacity: '100%' }}
            _focusWithin={{ opacity: '100%' }}
            css={{ '@media (hover: none)': { opacity: '100%' } }}
          >
            <DropdownActionButton
              icon={Ellipsis}
              variant={QuickActionButtonVariants.Default_100}
              size={Size.xs}
              tooltipText={t('contactCell.menu.moreOptions')}
              ariaLabel={actionsLabel}
              onMenuOpenChange={handleMenuOpenChange}
              menuOptions={{
                width: ContactCellStyleValues.menuWidth,
              }}
              items={menuItems}
            />
          </Box>
        )}
      </Flex>
    </Flex>
  )
}
